import { useLayoutEffect, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { styles } from './tabs.styles';
export interface TabItem<K extends string = string> {
  key: K;
  label: string;
}
interface TabsProps<K extends string> {
  tabs: readonly TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
}

/**
 * A focus the strip owes, kept at MODULE scope because it has to outlive the
 * component instance.
 *
 * ⚠⚠ THE STRIP IS OFTEN DESTROYED BY THE VERY CHANGE IT REQUESTED. Where `onChange`
 * navigates, the router unmounts the subtree and mounts a fresh one — measured: the
 * tablist node comes back `isConnected: false` and is replaced by a different
 * element. So neither a ref nor any per-instance state can carry the intent across
 * it, and the ORIGINAL bug was exactly this: the key handler focused the node it had
 * captured a line earlier, which was already detached, so `document.activeElement`
 * fell back to `<body>`. The first arrow key looked like it worked (tab and URL both
 * changed) and every one after it was swallowed. A layout effect on a ref does not
 * fix it either — the effect runs on the NEW instance, whose `wanted` is empty.
 *
 * So the request outlives the strip, and the next one to mount honours it.
 */
let owed: {
  index: number;
  count: number;
  at: number;
} | null = null;

/**
 * How long a request stays honourable.
 *
 * ⚠ 1000 was too short and failed at exactly one point: press a key while the route
 * is still resolving and the strip goes into a Suspense gap, so the replacement
 * mounts LATER than the request lived and the arrow key was swallowed after all.
 * Three seconds covers a chunk fetch on a slow farm connection.
 *
 * It is safe to be this generous only because the restore below refuses to take
 * focus from anything — it acts solely when focus sits on `<body>`, i.e. when the
 * remount dropped it and nothing else wants it. Without that guard a window this
 * wide would be a focus thief.
 */
const OWED_TTL_MS = 3000;

/** The page-level underline tab bar — one look everywhere (Settings, consoles, …). */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange
}: TabsProps<K>) {
  const bar = useRef<HTMLDivElement | null>(null);

  // Honour a focus the strip owes, on ANY render inside the request's lifetime.
  //
  // ⚠⚠ THIS IS SELF-HEALING BY DESIGN, AND THREE SIMPLER VERSIONS FAILED FIRST.
  // Focusing in the key handler failed because the node was already detached.
  // Focusing in a layout effect through a ref failed because the router MOUNTS A NEW
  // INSTANCE (measured: the old tablist comes back `isConnected: false`), so the ref
  // and every piece of per-instance state are gone. Consuming the request on the
  // first render that answered it failed INTERMITTENTLY — 4 runs in 5 — because this
  // page's queries settle while the route is changing, so a stray re-render lands
  // between the key press and the remount (~17ms), focuses the node it still holds,
  // consumes the request, and then the router discards that node and focus falls to
  // <body> with nothing left to ask again.
  //
  // So the request is neither consumed nor timed to a frame count: for its lifetime
  // every render checks whether focus has been LOST and takes it back. The outcome
  // stops depending on how many times the tree re-renders, or in what order.
  useLayoutEffect(() => {
    if (!owed) return;
    if (Date.now() - owed.at > OWED_TTL_MS) {
      owed = null;
      return;
    }
    // A different tab bar mounting cannot claim a request meant for this one.
    if (owed.count !== tabs.length) return;
    const el = bar.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[owed.index];
    if (!el) return;

    // ⚠ Only reclaim focus that is NOWHERE. Anything else means a human moved on —
    // Tab to the next control, a click elsewhere — and stealing it back would be
    // worse than the bug itself. Focus already inside this strip is the ordinary
    // case, and re-focusing the element that has it is a no-op.
    //
    // ⚠⚠ "Nowhere" INCLUDES A DETACHED ELEMENT, and leaving that out is what made the
    // last 1 run in 8 fail. Whether this effect sees `<body>` or still sees the OLD
    // tab depends on whether the removal has completed, and a detached old tab is
    // neither `<body>` nor inside the new strip — so the version without
    // `isConnected` read it as "the operator moved on" and threw the request away,
    // exactly when it was most needed. Focus on a node no longer in the document is
    // not focus.
    const active = document.activeElement;
    const lost = active === null || active === document.body || !active.isConnected;
    if (!lost && !bar.current?.contains(active)) {
      owed = null;
      return;
    }
    el.focus();
  });

  // Keep the SELECTED tab inside the strip's own scroll box. On a phone eleven
  // tabs overflow, and a tab opened by a deep link (`?tab=models`) sat off-screen
  // with nothing visible saying which one was active. Scrolls the strip only —
  // `scrollIntoView` would also move the page vertically.
  useLayoutEffect(() => {
    const b = bar.current;
    const el = b?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!b || !el || b.scrollWidth <= b.clientWidth) return;
    const br = b.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const pad = 16;
    if (er.left < br.left) b.scrollLeft -= br.left - er.left + pad;else if (er.right > br.right) b.scrollLeft += er.right - br.right + pad;
  }, [active]);

  /** Ask for the focus that belongs with `next`, then let the consumer change. The
   *  order matters: a navigating `onChange` never returns to this line. */
  const moveTo = (next: number) => {
    owed = {
      index: next,
      count: tabs.length,
      at: Date.now()
    };
    onChange(tabs[next]!.key);
  };

  // ARIA tabs pattern: roving tabindex + arrow keys move AND select.
  const onKeyDown = (e: KeyboardEvent) => {
    const idx = tabs.findIndex(t => t.key === active);
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;else if (e.key === 'Home') next = 0;else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    moveTo(next);
  };
  return <div ref={bar} {...stylex.props(styles.bar)} role="tablist">
      {tabs.map((t, i) => <button key={t.key} type="button" role="tab" aria-selected={t.key === active} tabIndex={t.key === active ? 0 : -1}
    // Through `moveTo` as well, so a CLICK leaves focus on the tab it selected
    // no matter what the consumer does with the change. A state-based consumer
    // gets that from the browser for free; a navigating one loses the button
    // mid-click and would drop focus to <body>, which is how an operator ends
    // up clicking a tab and finding the arrow keys dead. `:focus-visible` means
    // this paints no ring for the mouse.
    onClick={() => moveTo(i)} onKeyDown={onKeyDown} {...stylex.props(styles.tab, t.key === active && styles.tabActive)}>
          {t.label}
        </button>)}
    </div>;
}